import { computed, effect, Injector, runInInjectionContext, Signal, signal, untracked } from '@angular/core';
import { NotificationService } from '@app/notification/notification.service';
import { AutoStatus, MinesGame, RoundOutcome } from '@models/mines.interface';
import { AutoAction } from '@shared/auto-config/auto-config/auto-config.component';
import { SessionService } from '@shared/session.service';
import { SocketService } from '@shared/socket.service';
import type { MinesService } from './mines.service';

/**
 * Client-side auto-bet loop for mines. There are no auto-specific socket events:
 * every round is the same start_mines / select_mine / cashout_mines sequence a
 * manual round uses, driven in a loop with retry and cleanup around it.
 */
export class MinesAutoRunner {
  manual = signal<boolean>(true);
  /**
   * The player's full selection. Kept intact across mine-count changes so
   * tweaking the mines slider never discards picks.
   */
  preselected = signal<Set<number>>(new Set<number>());
  /**
   * The playable slice of `preselected` for the current mine count (at most
   * GRID_SIZE - mines safe tiles). Drives payout, board highlight and autoplay;
   * picks past the limit stay remembered and re-activate when it grows again.
   */
  activePreselected!: Signal<Set<number>>;
  numberOfBets = signal<number>(1);
  isInfinite = signal<boolean>(false);
  roundsRemaining = signal<number>(0);
  autoStatus = signal<AutoStatus>('idle');
  onWinAction = signal<AutoAction>('reset');
  onWinPercent = signal<number>(20);
  onLossAction = signal<AutoAction>('reset');
  onLossPercent = signal<number>(20);
  baseBet = signal<number>(0);
  sessionPnL = signal<number>(0);
  lastGameProfit = signal<number | null>(null);

  constructor(
    injector: Injector,
    private svc: MinesService,
    private socketService: SocketService,
    private session: SessionService,
    private notificationsService: NotificationService,
  ) {
    runInInjectionContext(injector, () => {
      effect(() => {
        this.manual();
        untracked(() => {
          if (this.autoStatus() !== 'idle') return;
          if (this.svc.currentGame()?.status === 'finished') {
            this.svc.resetBoard();
          }
          this.preselected.set(new Set<number>());
        });
      });

      this.activePreselected = computed(() => {
        const max = this.svc.GRID_SIZE - this.svc.mines();
        const all = this.preselected();
        if (all.size <= max) return all;
        return new Set([...all].slice(0, max));
      });
    });
  }

  togglePreselect(tile: number): void {
    if (this.autoStatus() !== 'idle') return;
    if (this.svc.currentGame().status === 'finished') {
      this.svc.resetBoard();
    }
    const max = this.svc.GRID_SIZE - this.svc.mines();
    this.preselected.update((prev) => {
      const next = new Set(prev);
      if (next.has(tile)) {
        next.delete(tile);
      } else if (next.size < max) {
        next.add(tile);
      }
      return next;
    });
  }

  handleTileClick(tile: number): void {
    if (this.autoStatus() === 'running' || this.autoStatus() === 'stopping') {
      return;
    }
    if (!this.manual()) {
      this.togglePreselect(tile);
    } else {
      // Fire-and-forget from a click handler; failures are already surfaced by
      // the socket layer, this just keeps the rejection from going unhandled.
      this.svc.checkMine(tile).catch(() => {});
    }
  }

  requestStop(): void {
    if (this.autoStatus() === 'running') {
      this.autoStatus.set('stopping');
    }
  }

  /**
   * Stop autoplay and leave nothing open server-side — used by the route guard.
   * Waits for the loop to finish the round it is in rather than emitting into
   * the middle of it; the forced close only covers the case where it doesn't.
   */
  async stopAndCleanup(): Promise<void> {
    this.requestStop();
    await this.waitForIdle();
    if (this.svc.currentGame().status === 'active') {
      await this.closeActiveGame();
    }
  }

  async runAutoLoop(): Promise<void> {
    if (this.autoStatus() !== 'idle') return;
    if (this.preselected().size === 0) {
      this.notificationsService.error('Pre-select at least one tile.', 'Mines');
      return;
    }

    this.baseBet.set(this.svc.amount());
    this.sessionPnL.set(0);
    this.autoStatus.set('running');
    this.roundsRemaining.set(this.isInfinite() ? Number.POSITIVE_INFINITY : this.numberOfBets());

    let exitedOnError = false;

    // Everything below runs under try/finally: if any step throws, autoStatus
    // must still return to 'idle' or the board stays locked with no way back.
    try {
      while (true) {
        if (this.autoStatus() !== 'running') break;
        if (!this.isInfinite() && this.roundsRemaining() <= 0) break;
        // Server-authoritative: mines never writes the balance locally, so this
        // is the only writer and the guard can trust it.
        if (this.svc.amount() > (this.session.currentBalance() ?? 0)) {
          this.notificationsService.error('Insufficient balance.', 'Mines');
          break;
        }

        const isLastRound = !this.isInfinite() && this.roundsRemaining() === 1;
        const outcome = await this.playAutoRound(isLastRound);
        if (outcome === 'error') {
          exitedOnError = true;
          this.notificationsService.error('Auto bet stopped — connection error', 'Mines');
          break;
        }

        this.applyBetAdjustment(outcome);

        if (!this.isInfinite()) {
          this.roundsRemaining.update((n) => n - 1);
        }

        if (this.autoStatus() === 'stopping') break;
      }
    } catch (err) {
      exitedOnError = true;
      console.error('[Mines] auto bet loop aborted', err);
      this.notificationsService.error('Auto bet stopped — unexpected error', 'Mines');
    } finally {
      this.roundsRemaining.set(0);

      if (exitedOnError) {
        await this.closeActiveGame();
      }

      this.autoStatus.set('idle');
    }
  }

  private async playAutoRound(isLastRound = false): Promise<RoundOutcome> {
    let started = false;
    for (let attempt = 0; attempt <= 3; attempt++) {
      try {
        started = await this.svc.placeBet(true);
        break;
      } catch {
        if (attempt >= 3) {
          return 'error';
        }
        // A leftover game server-side is the usual reason start_mines is refused.
        await this.closeActiveGame();
        await this.delay(1000);
      }
    }
    if (!started) return 'error';

    // Captured so a mid-round reconnect can be detected: the server binds the
    // game to the socket that created it.
    const socketId = this.socketService.socket.id;

    // The server action lock is not released at ack time; let it clear.
    await this.delay(0);
    const tiles = [...this.activePreselected()];
    const revealedTiles: number[] = [];
    let bust = false;
    let pendingMinesPositions: number[] | null = null;
    let pendingGameUpdate: Partial<MinesGame> | null = null;
    let cashoutFailed = false;

    for (const tile of tiles) {
      if (this.socketService.socket.id !== socketId) {
        return 'error';
      }

      let ack: MinesGame | number;
      let attempt = 0;
      while (true) {
        try {
          ack = await this.socketService.emit<MinesGame | number>('select_mine', { tile }, { noErrorMessage: true });
          break;
        } catch (err) {
          // Only the action-lock rejection is transient; anything else (timeout,
          // insufficient funds, closed game) aborts the run.
          if (!this.isActionLockError(err) || attempt >= 2) {
            return 'error';
          }
          attempt++;
          await this.delay(500);
        }
      }

      if (typeof ack !== 'number' && !this.svc.isFinalizedGameAck(ack)) {
        console.error('[Mines] Unexpected select_mine response during autoplay', ack);
        return 'error';
      }

      revealedTiles.push(tile);

      if (typeof ack !== 'number') {
        // An object means the server finalized the game — a bust, or a win it
        // closed itself on the last safe pick. Either way no cashout is needed.
        bust = !this.svc.resolveWon(ack, revealedTiles);
        if (!bust) {
          // That last pick was safe, so it still earns its multiplier step —
          // the P&L below reads this value.
          this.svc.multiplier.set(this.svc.calculateTileMultiplier(this.svc.GRID_SIZE, this.svc.mines(), revealedTiles.length - 1));
        }
        if (Array.isArray(ack.grid)) {
          pendingMinesPositions = this.svc.extractMinePositions(ack.grid);
        }
        const { grid, ...rest } = ack;
        // `won` is pinned from `bust` rather than left to the spread: the board
        // is not reset between auto rounds, so an ack that omits the flag would
        // otherwise inherit the previous round's result and show a win over a
        // loss that P&L already booked.
        pendingGameUpdate = { ...rest, status: 'finished', won: !bust };
        break;
      }

      this.svc.multiplier.set(this.svc.calculateTileMultiplier(this.svc.GRID_SIZE, this.svc.mines(), revealedTiles.length - 1));
      await this.delay(0);
    }

    if (!bust && pendingGameUpdate === null) {
      try {
        const game = await this.socketService.emit<MinesGame>('cashout_mines', undefined, {
          timeout: 5000,
          noErrorMessage: true,
        });
        if (Array.isArray(game.grid)) {
          pendingMinesPositions = this.svc.extractMinePositions(game.grid);
        }
        const { grid, ...rest } = game;
        // Pinned for the same reason as the select_mine branch above; a cashout
        // that acks is the win this round's P&L books.
        pendingGameUpdate = { ...rest, status: 'finished', won: true };
      } catch {
        const clean = await this.waitForCleanState();
        if (clean) {
          // The cashout ack was lost but the server has no game left, so it
          // settled the win without us. P&L below books a win we never saw
          // confirmed — the one place this runner infers an outcome.
          console.warn('[Mines] cashout ack lost; server reports no active game — booking the round as a win');
          pendingGameUpdate = { status: 'finished', won: true };
        } else {
          cashoutFailed = true;
        }
      }
    }

    // The game state has to land before `clicked` so status='finished' reaches
    // every tile before they start revealing.
    if (pendingGameUpdate) {
      this.svc.currentGame.update((prev) => ({ ...prev, ...pendingGameUpdate }));
    }
    if (pendingMinesPositions) {
      this.svc.minesPosition.set(pendingMinesPositions);
    }
    this.svc.clicked.set(revealedTiles);

    if (!cashoutFailed) {
      const bet = this.svc.amount();
      const payout = bust ? 0 : this.svc.multiplier() * bet;
      const profit = payout - bet;
      this.lastGameProfit.set(profit);
      this.sessionPnL.update((p) => p + profit);
      this.svc.lastWinPayout.set(bust ? null : payout);
    }

    // The final round keeps its result on screen instead of clearing it.
    const skipReset = isLastRound && this.autoStatus() !== 'stopping';
    if (!skipReset) {
      await this.delay(bust ? 800 : 700);
      this.svc.resetBoard();
      await this.delay(300);
    }

    if (cashoutFailed) return 'error';
    return bust ? 'loss' : 'win';
  }

  private applyBetAdjustment(outcome: 'win' | 'loss'): void {
    const action = outcome === 'win' ? this.onWinAction() : this.onLossAction();
    const pct = outcome === 'win' ? this.onWinPercent() : this.onLossPercent();

    this.svc.amount.update((curr) => {
      let next = curr;
      if (action === 'reset') {
        next = this.baseBet();
      } else if (action === 'increase') {
        next = curr * (1 + pct / 100);
      }
      next = Math.min(next, this.svc.MAX_BET);
      next = Math.max(next, this.svc.MIN_BET);
      return Math.round(next * 100) / 100;
    });
  }

  /**
   * Clears whatever game the server still holds, so the next start_mines isn't
   * refused. Every step here is expected to fail sometimes and stays quiet.
   */
  private async closeActiveGame(): Promise<void> {
    try {
      // Works as long as at least one tile was played.
      await this.svc.checkout(true);
      return;
    } catch {}

    try {
      const game = await this.socketService.emit('load_mines', undefined, { noErrorMessage: true });
      if (!game) return;
      // No tiles played yet — force-pick one so the game can be resolved.
      const ack = await this.socketService.emit('select_mine', { tile: 1 }, { noErrorMessage: true });
      await this.delay(300);
      if (typeof ack === 'number') {
        try {
          await this.svc.checkout(true);
        } catch {}
      }
      await this.waitForCleanState();
    } catch {}
  }

  private async waitForCleanState(maxAttempts = 10): Promise<boolean> {
    for (let i = 0; i < maxAttempts; i++) {
      await this.delay(300);
      try {
        const game = await this.socketService.emit('load_mines', undefined, { noErrorMessage: true });
        if (!game) return true;
      } catch {}
    }
    return false;
  }

  private isActionLockError(err: unknown): boolean {
    let message = '';
    if (typeof err === 'string') {
      message = err;
    } else if (err instanceof Error) {
      message = err.message;
    } else if (err && typeof err === 'object') {
      message = String((err as { message?: unknown; error?: unknown }).message ?? (err as { error?: unknown }).error ?? '');
    }
    return message.toLowerCase().includes('active game');
  }

  private async waitForIdle(timeoutMs = 3000): Promise<void> {
    const deadline = Date.now() + timeoutMs;
    while (this.autoStatus() !== 'idle' && Date.now() < deadline) {
      await this.delay(100);
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
